import React, { useState, useEffect } from 'react';
import { ASSETS } from '../assets/assetMapping';
import { fetchPricingData, ProcessedPricing } from '../services/pricingService';
import { mixpanelService } from '../services/mixpanelService';

interface PoppingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  onSubscribe?: () => void;
  onAlreadySubscribed?: () => void;
  showTimeline?: boolean;
}

const PoppingModal: React.FC<PoppingModalProps> = ({
  isOpen,
  onClose,
  title = "Free Custom Avatar & AI Pro",
  subtitle = "Subscribe to AI Pro to create a custom avatar from BIGVU's global catalog. Use on iPhone, Android, and Web.",
  onSubscribe,
  onAlreadySubscribed,
  showTimeline = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [pricingData, setPricingData] = useState<ProcessedPricing | null>(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Fetch pricing data when modal opens
      if (!pricingData) {
        setIsLoadingPricing(true);
        fetchPricingData()
          .then((data) => {
            setPricingData(data);
          })
          .catch((error) => {
            console.error('Failed to fetch pricing data:', error);
          })
          .finally(() => {
            setIsLoadingPricing(false);
          });
      }
    } else {
      setIsVisible(false);
    }
  }, [isOpen, pricingData]);

  const handleSubscribe = () => {
    // Track convert event with stock-avatar trigger and selected plan duration
    mixpanelService.trackConvert('stock-avatar', selectedPlan);
    
    if (onSubscribe) {
      onSubscribe();
    } else {
      // Default behavior - redirect to BigVu signup
      window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=subscribe&utm_medium=modal&utm_source=prompt2avatar', '_blank');
    }
  };

  const handleAlreadySubscribed = () => {
    if (onAlreadySubscribed) {
      onAlreadySubscribed();
    } else {
      // Default behavior - redirect to BigVu login
      window.open('https://desk.bigvu.tv/login?utm_campaign=avatar_generator&utm_content=already_subscribed&utm_medium=modal&utm_source=prompt2avatar', '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-0">
      {/* Background Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className={`relative bg-gradient-to-l from-[#f4fbfe] from-[2.372%] to-[#f6f8ff] to-[99.351%] rounded-[20px] w-full max-w-[960px] h-auto min-h-[601px] max-h-[90vh] lg:w-[960px] lg:h-[601px] overflow-hidden transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Background Decorations - Desktop Only */}
        <div className="hidden lg:block absolute h-[348px] top-[309px] left-[calc(50%-386.116px)] w-[384px]">
          <div className="absolute inset-[-98.24%_-89.03%] opacity-20">
            <div className="w-full h-full bg-gradient-to-r from-blue-200 to-purple-200 rounded-full blur-3xl" />
          </div>
        </div>
        <div className="hidden lg:block absolute h-[273px] mix-blend-overlay top-[384px] left-[calc(50%-148.116px)] w-[238px]">
          <div className="absolute inset-[-125.23%_-143.64%] opacity-30">
            <div className="w-full h-full bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur-3xl" />
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-90 hover:bg-opacity-100 shadow-lg transition-all"
        >
          <svg className="w-4 h-4 text-[#253658]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Mobile Layout */}
        <div className="lg:hidden flex flex-col h-full">
          {/* Mobile Header Section */}
          <div className="bg-white rounded-t-[16px] p-6 shadow-lg">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-[28px] font-bold text-[#253658] mb-2 leading-tight">
                {title}
              </h1>
              <p className="text-[14px] text-[#253658] leading-[1.4]">
                {subtitle}
              </p>
            </div>

            {/* Plan Selector */}
            <div className="mb-6">
              <div className="bg-[rgba(68,129,253,0.15)] rounded-[100px] p-1 flex h-[32px]">
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`flex-1 py-1 px-3 rounded-[100px] text-[12px] font-semibold transition-all ${
                    selectedPlan === 'monthly' 
                      ? 'bg-[#253658] text-white' 
                      : 'text-[#253658] hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSelectedPlan('yearly')}
                  className={`flex-1 py-1 px-3 rounded-[100px] text-[12px] font-semibold transition-all ${
                    selectedPlan === 'yearly' 
                      ? 'bg-[#253658] text-white' 
                      : 'text-[#253658] hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>

            {/* Pricing */}
            <div className="mb-6">
              {isLoadingPricing ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4481fd]"></div>
                </div>
              ) : pricingData ? (
                selectedPlan === 'yearly' ? (
                  <>
                    <div className="flex items-end gap-2 mb-1">
                      <span className="text-[48px] font-semibold text-[#253658] leading-none">
                        {pricingData.yearly.currencySymbol}{pricingData.yearly.monthlyEquivalent}
                      </span>
                      <div className="flex flex-col pb-3">
                        <span className="text-[16px] text-[#a9b0bf] line-through">
                          {pricingData.yearly.currencySymbol}{pricingData.yearly.originalPrice}
                        </span>
                        <span className="text-[16px] text-[#253658] font-bold">
                          / mo
                        </span>
                      </div>
                    </div>
                    <p className="text-[14px] font-medium text-[#253658]">
                      {pricingData.yearly.billingText}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-end gap-2 mb-1">
                      <span className="text-[48px] font-semibold text-[#253658] leading-none">
                        {pricingData.monthly.currencySymbol}{pricingData.monthly.price}
                      </span>
                      <div className="flex flex-col pb-3">
                        <span className="text-[16px] text-[#253658] font-bold">
                          / mo
                        </span>
                      </div>
                    </div>
                    <p className="text-[14px] font-medium text-[#253658]">
                      {pricingData.monthly.billingText}
                    </p>
                  </>
                )
              ) : (
                <div className="text-center text-[#253658]">
                  <p className="text-[14px]">Pricing unavailable</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleSubscribe}
                className="w-full bg-[#4481fd] hover:bg-blue-600 text-white font-bold text-[16px] py-4 px-4 rounded-[12px] transition-colors shadow-lg"
              >
                Subscribe & Create Avatar
              </button>
              <button
                onClick={handleAlreadySubscribed}
                className="w-full bg-transparent hover:bg-white hover:bg-opacity-10 text-[#253658] font-semibold text-[14px] py-4 px-4 rounded-[12px] transition-colors"
              >
                already subscribed?
              </button>
            </div>
          </div>

          {/* Mobile Video Section */}
          <div className="relative flex-1 bg-gradient-to-b from-[#f4fbfe] to-[#f6f8ff] p-4">
            <div className="relative w-full h-[200px] rounded-[16px] overflow-hidden shadow-lg">
              <img 
                src={ASSETS.VIDEO_IMAGE} 
                alt="AI Avatar Demo" 
                className="w-full h-full object-cover"
              />
              
              {/* Free Avatar Badge - Mobile */}
              <div className="absolute top-4 right-4 bg-gradient-to-r from-[#28b3f7] to-[#506aff] rounded-[12px] px-3 py-2 shadow-lg w-[120px] h-[56px]">
                <div className="text-center text-white">
                  <p className="text-[12px] font-medium">Subscribe & get</p>
                  <p className="text-[16px] font-bold">Free Avatar</p>
                </div>
              </div>
            </div>

            {/* Mobile Timeline Section */}
            {showTimeline && (
              <div className="mt-6">
                {/* Timeline Icons Row */}
                <div className="flex items-center justify-center mb-4">
                  {/* First Icon - Checkmark */}
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  {/* First Connector Line */}
                  <div className="w-[80px] h-px bg-[rgba(37,54,88,0.2)]" />
                  
                  {/* Second Icon - Crown */}
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  </div>
                  {/* Second Connector Line */}
                  <div className="w-[80px] h-px bg-[rgba(37,54,88,0.2)]" />
                  
                  {/* Third Icon - Forever/Infinity */}
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>
                      <path d="M10 6c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                    </svg>
                  </div>
                </div>
                
                {/* Timeline Labels */}
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-[14px] font-medium text-[#253658] mb-1">Today</p>
                    <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Validate the avatar portrait you want</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[14px] font-medium text-[#253658] mb-1">Subscribe to Pro</p>
                    <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Unlock premium features + create your avatar</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[14px] font-medium text-[#253658] mb-1">Forever</p>
                    <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Your avatar is added to BIGVU's official stock catalog</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout - Left Content Card */}
        <div className="hidden lg:block absolute bg-white left-[48px] top-[48px] w-[341px] h-[505px] rounded-[16px] p-6 shadow-lg">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-[40px] font-bold text-[#253658] mb-2 leading-tight">
              {title}
            </h1>
            <p className="text-[14px] text-[#253658] leading-[1.4]">
              {subtitle}
            </p>
          </div>

          {/* Plan Selector */}
          <div className="mb-6">
            <div className="bg-[rgba(68,129,253,0.15)] rounded-[100px] p-1 flex h-[32px]">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`flex-1 py-1 px-3 rounded-[100px] text-[12px] font-semibold transition-all ${
                  selectedPlan === 'monthly' 
                    ? 'bg-[#253658] text-white' 
                    : 'text-[#253658] hover:bg-white hover:bg-opacity-20'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`flex-1 py-1 px-3 rounded-[100px] text-[12px] font-semibold transition-all ${
                  selectedPlan === 'yearly' 
                    ? 'bg-[#253658] text-white' 
                    : 'text-[#253658] hover:bg-white hover:bg-opacity-20'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-6">
            {isLoadingPricing ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4481fd]"></div>
              </div>
            ) : pricingData ? (
              selectedPlan === 'yearly' ? (
                <>
                  <div className="flex items-end gap-2 mb-1">
                    <span className="text-[72px] font-semibold text-[#253658] leading-none">
                      {pricingData.yearly.currencySymbol}{pricingData.yearly.monthlyEquivalent}
                    </span>
                    <div className="flex flex-col pb-3">
                      <span className="text-[20px] text-[#a9b0bf] line-through">
                        {pricingData.yearly.currencySymbol}{pricingData.yearly.originalPrice}
                      </span>
                      <span className="text-[20px] text-[#253658] font-bold">
                        / mo
                      </span>
                    </div>
                  </div>
                  <p className="text-[14px] font-medium text-[#253658]">
                    {pricingData.yearly.billingText}
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-end gap-2 mb-1">
                    <span className="text-[72px] font-semibold text-[#253658] leading-none">
                      {pricingData.monthly.currencySymbol}{pricingData.monthly.price}
                    </span>
                    <div className="flex flex-col pb-3">
                      <span className="text-[20px] text-[#253658] font-bold">
                        / mo
                      </span>
                    </div>
                  </div>
                  <p className="text-[14px] font-medium text-[#253658]">
                    {pricingData.monthly.billingText}
                  </p>
                </>
              )
            ) : (
              // Fallback display if pricing data fails to load
              <div className="text-center text-[#253658]">
                <p className="text-[14px]">Pricing unavailable</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleSubscribe}
              className="w-full bg-[#4481fd] hover:bg-blue-600 text-white font-bold text-[18px] py-4 px-4 rounded-[12px] transition-colors shadow-lg"
            >
              Subscribe & Create Avatar
            </button>
            <button
              onClick={handleAlreadySubscribed}
              className="w-full bg-transparent hover:bg-white hover:bg-opacity-10 text-[#253658] font-semibold text-[14px] py-4 px-4 rounded-[12px] transition-colors"
            >
              already subscribed?
            </button>
          </div>
        </div>

        {/* Desktop Layout - Right Content - Video/Image */}
        <div className="hidden lg:block absolute right-[48px] top-[48px] w-[507px] h-[281px] rounded-[16px] overflow-hidden">
          <img 
            src={ASSETS.VIDEO_IMAGE} 
            alt="AI Avatar Demo" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Desktop Layout - Free Avatar Badge */}
        <div className="hidden lg:block absolute top-[69px] right-[64px] bg-gradient-to-r from-[#28b3f7] to-[#506aff] rounded-[12px] px-4 py-2 shadow-lg w-[148px] h-[64px]">
          <div className="text-center text-white">
            <p className="text-[14px] font-medium">Subscribe & get</p>
            <p className="text-[20px] font-bold">Free Avatar</p>
          </div>
          {/* Ribbon decoration */}
          {/* <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-yellow-400" /> */}
        </div>

        {/* Desktop Layout - Timeline Section */}
        {showTimeline && (
          <div className="hidden lg:block absolute top-[380px] left-[470px] w-[500px]">
            {/* Timeline Icons Row */}
            <div className="flex items-center mb-2">
              {/* First Icon - Checkmark */}
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              {/* First Connector Line */}
              <div className="w-[137px] h-px bg-[rgba(37,54,88,0.2)]" />
              
              {/* Second Icon - Crown */}
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
              {/* Second Connector Line */}
              <div className="w-[137px] h-px bg-[rgba(37,54,88,0.2)]" />
              
              {/* Third Icon - Forever/Infinity */}
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-[#4481fd]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>
                  <path d="M10 6c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                </svg>
              </div>
            </div>
            
            {/* Timeline Labels */}
            <div className="flex">
              <div className="w-[153px]">
                <p className="text-[14px] font-medium text-[#253658] mb-1">Today</p>
                <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Validate the avatar portrait you want</p>
              </div>
              <div className="w-[160px]">
                <p className="text-[14px] font-medium text-[#253658] mb-1">Subscribe to Pro</p>
                <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Unlock premium features + create your avatar</p>
              </div>
              <div className="w-[153px]">
                <p className="text-[14px] font-medium text-[#253658] mb-1">Forever</p>
                <p className="text-[14px] text-[rgba(37,54,88,0.6)]">Your avatar is added to BIGVU's official stock catalog</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoppingModal;
